import React, { useEffect, useState, useRef } from 'react';
import { ComponentLoader } from '../common/Loader';
import { useDispatch } from 'react-redux';
import { fetchModuleUsageStats, fetchTotalUsers } from "./store";
import { FaAppStoreIos, FaChrome, FaStethoscope, FaUser, FaUsers, FaUserPlus, FaApple, FaHeartbeat, FaCalendarAlt, FaWalking } from "react-icons/fa";
import { FaDroplet } from "react-icons/fa6";
import { HiScale } from "react-icons/hi";
import { TbTriangleSquareCircleFilled, TbMoodSmileFilled } from "react-icons/tb";
import { MdBloodtype } from "react-icons/md";
import { IoIosBookmarks, IoIosDocument } from "react-icons/io";
import { LuMicroscope } from "react-icons/lu";

const OverallModuleUsage = () => {
  const dispatch = useDispatch();
  const [moduleUsageStats, setModuleUsageStats] = useState(null);
  const [totalUsers, setTotalUsers] = useState({});
  const [loadingModuleStats, setLoadingModuleStats] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedModule, setSelectedModule] = useState('periodTracking');
  const dataFetchedRef = useRef(false);

  const loadModuleData = async () => {
    const cacheKey = 'MODULE_USAGE_CACHE';
    let cacheExists = false;

    const cachedData = sessionStorage.getItem(cacheKey);
    if (cachedData) {
      const data = JSON.parse(cachedData);
      if (data === 'PENDING') {

        return;
      }

      if (data && typeof data === 'object') {
        const cacheTimestamp = data.cacheTimestamp || 0;
        const threeMinutes = 3 * 60 * 1000;
        const isExpired = Date.now() - cacheTimestamp > threeMinutes;
        if (!isExpired) {

          setModuleUsageStats(data.moduleUsageStats);
          setTotalUsers(data.totalUsers);
          dataFetchedRef.current = true;
          return;
        } else {
          setModuleUsageStats(data.moduleUsageStats);
          setTotalUsers(data.totalUsers);
          cacheExists = true;
        }
      }
    }

    sessionStorage.setItem(cacheKey, JSON.stringify('PENDING'));
    dataFetchedRef.current = true;

    if (!cacheExists) {
      setLoadingModuleStats(true);
      setLoadingUsers(true);
    }

    try {
      const [usageRes, totalRes] = await Promise.all([
        dispatch(fetchModuleUsageStats()),
        dispatch(fetchTotalUsers())
      ]);

      const cache = {};

      if (usageRes.payload && usageRes.payload.success) {
        setModuleUsageStats(usageRes.payload.data);
        cache.moduleUsageStats = usageRes.payload.data;
      }

      if (totalRes.payload && totalRes.payload.success) {
        setTotalUsers(totalRes.payload.data);
        cache.totalUsers = totalRes.payload.data;
      }

      if (Object.keys(cache).length > 0) {
        cache.cacheTimestamp = Date.now();
        sessionStorage.setItem(cacheKey, JSON.stringify(cache));
      } else {
        sessionStorage.removeItem(cacheKey);
      }
    } catch (err) {
      console.error("Error fetching overall module usage data:", err);
      sessionStorage.removeItem(cacheKey);
    } finally {
      setLoadingModuleStats(false);
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    loadModuleData();
  }, [dispatch]);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <div style={{ width: '6px', height: '24px', background: 'linear-gradient(to bottom, #4f46e5, #3b82f6)', borderRadius: '3px' }}></div>
        <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#1a1a1a', letterSpacing: '-0.5px' }}>Overall Module Usage</h3>
      </div>

      <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
        <div style={{ flex: '1', minWidth: '250px', display: 'flex', flexDirection: 'column', gap: '12px', position: 'relative' }}>
          {loadingUsers && (
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.7)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
              <ComponentLoader />
            </div>
          )} 
          {[
            { id: 'periodTracking', name: 'Period Tracker', icon: <MdBloodtype />, color: '#ec4899', count: totalUsers?.periodTracking || 0 },
            { id: 'moodLogs', name: 'Mood Logs', icon: <TbMoodSmileFilled />, color: '#3b82f6', count: totalUsers?.moodLogs || 0 },
            { id: 'stepsTracker', name: 'Steps Tracker', icon: <FaWalking />, color: '#10b981', count: totalUsers?.stepsTracker || 0 },
            { id: 'waterIntake', name: 'Hydration Tracker', icon: <FaDroplet />, color: '#0ea5e9', count: totalUsers?.waterIntake || 0 },
            { id: 'medicalDocuments', name: 'Medical Documents', icon: <IoIosDocument />, color: '#6366f1', count: totalUsers?.medicalDocuments || 0 },
            { id: 'bloodPressure', name: 'Blood Pressure', icon: <FaHeartbeat />, color: '#ef4444', count: totalUsers?.bloodPressure || 0 },
            { id: 'bloodSugar', name: 'Blood Sugar', icon: <LuMicroscope />, color: '#9333ea', count: totalUsers?.bloodSugar || 0 },
          ].map((mod) => (
            <div
              key={mod.id}
              onClick={() => setSelectedModule(mod.id)}
              style={{
                padding: '20px',
                background: selectedModule === mod.id ? mod.color : '#fff',
                color: selectedModule === mod.id ? '#fff' : '#4b5563',
                borderRadius: '16px',
                cursor: 'pointer',
                boxShadow: selectedModule === mod.id ? `0 10px 15px -3px ${mod.color}44` : '0 1px 3px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: selectedModule === mod.id ? 'scale(1.02)' : 'scale(1)',
                border: selectedModule === mod.id ? 'none' : '1px solid #f1f5f9'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <div style={{
                  fontSize: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  color: selectedModule === mod.id ? '#fff' : mod.color
                }}>
                  {mod.icon}
                </div>
                <span style={{ fontWeight: 600, fontSize: '15px' }}>{mod.name}</span>
              </div>
              <div style={{
                background: selectedModule === mod.id ? 'rgba(255,255,255,0.2)' : '#f8fafc',
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '13px',
                fontWeight: 700
              }}>
                {mod.count} Users
              </div>
            </div>
          ))}
        </div>

        <div style={{ flex: '4', minWidth: '500px', background: '#fff', borderRadius: '24px', padding: '32px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #f1f5f9', position: 'relative', minHeight: '400px' }}>
          {loadingModuleStats && (
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.7)', borderRadius: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
              <ComponentLoader />
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
              {[
                { label: 'Total Logs', value: moduleUsageStats?.[selectedModule]?.totalDocs || 0, sub: '(Total document count)', color: '#4f46e5', icon: <IoIosDocument /> },
                { label: 'Current Month Logs', value: moduleUsageStats?.[selectedModule]?.monthDocs || 0, sub: `${new Date().toLocaleString('default', { month: 'long' })} 1 - Today`, color: '#10b981', icon: <FaCalendarAlt /> },
                { label: "Today's Logs", value: moduleUsageStats?.[selectedModule]?.todayDocs || 0, sub: 'Activity today', color: '#f59e0b', icon: <TbTriangleSquareCircleFilled /> }
              ].map((stat, i) => (
                <div key={i} style={{ background: '#f8fafc', borderRadius: '20px', padding: '24px', border: '1px solid #f1f5f9' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <span style={{ fontSize: '14px', fontWeight: 600, color: '#64748b' }}>{stat.label}</span>
                    <div style={{ color: stat.color, fontSize: '20px' }}>{stat.icon}</div>
                  </div>
                  <div style={{ fontSize: '32px', fontWeight: 800, color: '#1e293b', marginBottom: '4px' }}>{stat.value}</div>
                  <div style={{ fontSize: '11px', color: '#94a3af', fontWeight: 500 }}>{stat.sub}</div>
                </div>
              ))}
            </div>

            <div style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', borderRadius: '24px', padding: '32px', border: '1px solid #e2e8f0' }}>
              <h4 style={{ margin: '0 0 24px 0', fontSize: '16px', fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#4f46e5' }}></div>
                Module Insights
              </h4>

              {selectedModule === 'periodTracking' && (
                <div style={{ display: 'flex', gap: '40px', flexWrap: 'wrap' }}>
                  <div style={{ flex: '0 0 200px' }}>
                    <p style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: 600, color: '#64748b' }}>Avg Cycle Length</p>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', background: '#fff', padding: '16px 20px', borderRadius: '20px', border: '1px solid #fce7f3' }}>
                      <span style={{ fontSize: '36px', fontWeight: 800, color: '#db2777' }}>{moduleUsageStats?.periodTracking?.avgCycleLength}</span>
                      <span style={{ fontSize: '15px', fontWeight: 600, color: '#f472b6' }}>days</span>
                    </div>
                  </div>

                  <div style={{ flex: 2 }}>
                    <p style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: 600, color: '#64748b' }}>Top 5 Symptoms</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {moduleUsageStats?.periodTracking?.top5Symptoms?.length > 0 ? (
                        moduleUsageStats.periodTracking.top5Symptoms.map((symp, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <div style={{ minWidth: '100px', fontSize: '13px', fontWeight: 600, color: '#4b5563' }}>{symp.name}</div>
                            <div style={{ flex: 1, height: '8px', background: '#fdf2f8', borderRadius: '4px', overflow: 'hidden' }}>
                              <div style={{
                                height: '100%',
                                width: `${Math.min(100, (symp.count / (moduleUsageStats.periodTracking.totalDocs || 1)) * 100)}%`,
                                background: 'linear-gradient(to right, #f472b6, #db2777)',
                                borderRadius: '4px'
                              }}></div>
                            </div>
                            <div style={{ minWidth: '30px', fontSize: '12px', fontWeight: 700, color: '#9d174d' }}>{symp.count}</div>
                          </div>
                        ))
                      ) : (
                        <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '14px', background: '#f8fafc', borderRadius: '12px' }}>
                          No symptoms data recorded yet
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {selectedModule === 'moodLogs' && (
                <div style={{ display: 'flex', gap: '60px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 600, color: '#64748b' }}>Most Common Mood</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <span style={{ fontSize: '42px' }}>{moduleUsageStats?.moodLogs?.mostCommonMood === 'Happy' ? '😊' : moduleUsageStats?.moodLogs?.mostCommonMood === 'Sadness' ? '😔' : '😌'}</span>
                      <span style={{ fontSize: '24px', fontWeight: 800, color: '#3b82f6' }}>{moduleUsageStats?.moodLogs?.mostCommonMood}</span>
                    </div>
                  </div>
                  <div style={{ flex: 1, background: '#fff', padding: '16px 24px', borderRadius: '20px', border: '1px solid #e2e8f0' }}>
                    <p style={{ margin: '0 0 4px 0', fontSize: '12px', fontWeight: 700, color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.5px' }}>High Stress Logs</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '32px', fontWeight: 900, color: '#1e293b' }}>{moduleUsageStats?.moodLogs?.highStressCount}</span>
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444', boxShadow: '0 0 8px #ef4444' }}></div>
                    </div>
                  </div>
                </div>
              )}

              {selectedModule === 'stepsTracker' && (
                <div style={{ display: 'flex', gap: '40px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 600, color: '#64748b' }}>Avg Steps/Day</p>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                      <span style={{ fontSize: '36px', fontWeight: 800, color: '#10b981' }}>{moduleUsageStats?.stepsTracker?.avgSteps?.toLocaleString()}</span>
                      <span style={{ fontSize: '14px', fontWeight: 600, color: '#94a3af' }}>steps</span>
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 600, color: '#64748b' }}>Avg Daily Goal</p>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                      <span style={{ fontSize: '36px', fontWeight: 800, color: '#059669' }}>{moduleUsageStats?.stepsTracker?.avgStepGoal?.toLocaleString()}</span>
                      <span style={{ fontSize: '14px', fontWeight: 600, color: '#94a3af' }}>steps</span>
                    </div>
                  </div>
                </div>
              )}

              {selectedModule === 'waterIntake' && (
                <div style={{ display: 'flex', gap: '40px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 600, color: '#64748b' }}>Avg Water Intake</p>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                      <span style={{ fontSize: '36px', fontWeight: 800, color: '#0ea5e9' }}>{moduleUsageStats?.waterIntake?.avgWater?.toLocaleString()}</span>
                      <span style={{ fontSize: '14px', fontWeight: 600, color: '#94a3af' }}>ml</span>
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 600, color: '#64748b' }}>Avg Target</p>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                      <span style={{ fontSize: '36px', fontWeight: 800, color: '#0369a1' }}>{moduleUsageStats?.waterIntake?.avgWaterTarget?.toLocaleString()}</span>
                      <span style={{ fontSize: '14px', fontWeight: 600, color: '#94a3af' }}>ml</span>
                    </div>
                  </div>
                </div>
              )}

              {selectedModule === 'medicalDocuments' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#64748b' }}>Top 5 Document Categories</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {moduleUsageStats?.medicalDocuments?.top5DocTypes?.length > 0 ? (
                      moduleUsageStats.medicalDocuments.top5DocTypes.map((doc, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                          <div style={{ minWidth: '180px', fontSize: '13px', fontWeight: 600, color: '#4b5563' }}>{doc.name}</div>
                          <div style={{ flex: 1, height: '8px', background: '#eef2ff', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{
                              height: '100%',
                              width: `${Math.min(100, (doc.count / (moduleUsageStats.medicalDocuments.totalDocs || 1)) * 100)}%`,
                              background: 'linear-gradient(to right, #818cf8, #6366f1)',
                              borderRadius: '4px'
                            }}></div>
                          </div>
                          <div style={{ minWidth: '40px', textAlign: 'right', fontSize: '12px', fontWeight: 800, color: '#6366f1' }}>{doc.count}</div>
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: '30px', textAlign: 'center', color: '#94a3af', background: '#f8fafc', borderRadius: '16px', border: '1px dashed #cbd5e1' }}>
                        No medical documents categorized yet
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedModule === 'bloodPressure' && (
                <div style={{ display: 'flex', gap: '40px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 600, color: '#64748b' }}>Average Systolic</p>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                      <span style={{ fontSize: '36px', fontWeight: 800, color: '#ef4444' }}>{moduleUsageStats?.bloodPressure?.avgSystolic}</span>
                      <span style={{ fontSize: '14px', fontWeight: 600, color: '#94a3af' }}>mmHg</span>
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 600, color: '#64748b' }}>Average Diastolic</p>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                      <span style={{ fontSize: '36px', fontWeight: 800, color: '#f87171' }}>{moduleUsageStats?.bloodPressure?.avgDiastolic}</span>
                      <span style={{ fontSize: '14px', fontWeight: 600, color: '#94a3af' }}>mmHg</span>
                    </div>
                  </div>
                </div>
              )}

              {selectedModule === 'bloodSugar' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '20px' }}>
                  {[
                    { label: 'Low', count: moduleUsageStats?.bloodSugar?.breakdown?.Low || 0, color: '#6b7280' },
                    { label: 'Normal', count: moduleUsageStats?.bloodSugar?.breakdown?.Normal || 0, color: '#10b981' },
                    { label: 'Pre-diabetes', count: moduleUsageStats?.bloodSugar?.breakdown?.Prediabetes || 0, color: '#f59e0b' },
                    { label: 'Diabetes', count: moduleUsageStats?.bloodSugar?.breakdown?.Diabetes || 0, color: '#ef4444' }
                  ].map((item, i) => (
                    <div key={i} style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#64748b', marginBottom: '8px' }}>{item.label}</div>
                      <div style={{ fontSize: '28px', fontWeight: 800, color: item.color }}>{item.count}</div>
                      <div style={{ height: '4px', width: '40px', background: item.color, margin: '8px auto', borderRadius: '2px' }}></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default OverallModuleUsage